<template>
  <div>
    <EngagementLiveSection
      :live-windows="liveWindows"
      :active-live-window="activeLiveWindow"
      :live-loading="liveLoading"
      :live-error="liveError"
      :live-stats="liveStats"
      :format-window-fn="formatWindowFn"
      @set-live-window="$emit('set-live-window', $event)"
    />

    <EngagementHistoricalControlsSection
      :is-all-scope="isAllScope"
      :allow-all-scope-historical="allowAllScopeHistorical"
      :presets="presets"
      :active-preset="activePreset"
      :toggle-custom="toggleCustom"
      :custom-from="customFrom"
      :custom-to="customTo"
      :exporting="exporting"
      :export-error="exportError"
      :export-status-text="exportStatusText"
      :export-button-label="exportButtonLabel"
      :loading="loading"
      :breakdowns-loading="breakdownsLoading"
      :engagement-insights-loading="engagementInsightsLoading"
      :is-ceremony-breakdown-loading="isCeremonyBreakdownLoading"
      :engagement-primary-loading-text="engagementPrimaryLoadingText"
      @apply-preset="$emit('apply-preset', $event)"
      @update:toggleCustom="$emit('update:toggleCustom', $event)"
      @update:customFrom="$emit('update:customFrom', $event)"
      @update:customTo="$emit('update:customTo', $event)"
      @apply-custom="$emit('apply-custom')"
      @load-engagement="$emit('load-engagement')"
      @export-report="$emit('export-report')"
    />

    <div v-if="loading">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-pulse">
        <div v-for="i in 6" :key="`sk-card-${i}`" class="rounded-xl p-5 border bg-white">
          <div class="h-3 w-24 bg-gray-200 rounded mb-3"></div>
          <div class="h-7 w-20 bg-gray-300 rounded"></div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
        <div>
          <div class="h-4 w-40 bg-gray-200 rounded mb-4"></div>
          <div v-for="i in 4" :key="`sk-share-${i}`" class="mb-3">
            <div class="flex justify-between mb-2">
              <div class="h-3 w-24 bg-gray-200 rounded"></div>
              <div class="h-3 w-10 bg-gray-200 rounded"></div>
            </div>
            <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div class="h-2 bg-gray-200 rounded-full" style="width: 60%"></div>
            </div>
          </div>
        </div>
        <div>
          <div class="h-4 w-32 bg-gray-200 rounded mb-4"></div>
          <div class="h-3 w-52 bg-gray-100 rounded mb-3"></div>
          <div v-for="i in 4" :key="`sk-tz-${i}`" class="mb-3">
            <div class="flex justify-between mb-2">
              <div class="h-3 w-28 bg-gray-200 rounded"></div>
              <div class="h-3 w-8 bg-gray-200 rounded"></div>
            </div>
            <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div class="h-2 bg-gray-200 rounded-full" style="width: 45%"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-8 animate-pulse">
        <div class="h-4 w-36 bg-gray-200 rounded mb-4"></div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div v-for="i in 5" :key="`sk-src-${i}`" class="mb-3">
              <div class="flex justify-between mb-2">
                <div class="h-3 w-40 bg-gray-200 rounded"></div>
                <div class="h-3 w-10 bg-gray-200 rounded"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 bg-gray-200 rounded-full" style="width: 50%"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="errorMsg" class="text-red-600 text-sm">
      Failed to load analytics. {{ errorMsg }}
      <button @click="$emit('load-engagement')" class="ml-2 underline">Retry</button>
    </div>

    <div v-else-if="stats">
      <div v-if="engagementHeadlineSummary" class="border rounded-lg bg-white p-4 mb-6">
        <div class="text-sm font-semibold text-gray-900 mb-1">{{ engagementHeadlineSummary.title }}</div>
        <div class="text-sm text-gray-700">{{ engagementHeadlineSummary.summary }}</div>
        <div class="text-xs text-gray-500 mt-2">Relevant to selected scope: {{ engagementSelectedScopeLabel }}</div>
      </div>

      <EngagementKpiCards :stats="stats" :tooltips="tooltips" :show-benchmark="isAdmin" />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 class="text-lg font-semibold mb-3">Share distribution <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.share_distribution" role="img" aria-label="Info">ⓘ</span></h3>
          <div v-if="(stats.share_platforms || []).length === 0" class="text-sm text-gray-500">No share data in range.</div>
          <div v-else>
            <div v-for="row in stats.share_platforms" :key="row.platform" class="mb-3">
              <div class="flex justify-between text-sm mb-1"><span class="capitalize">{{ row.platform }}</span><span>{{ row.percent }}%</span></div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: row.percent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-lg font-semibold mb-3">Timezones <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.timezones" role="img" aria-label="Info">ⓘ</span></h3>
          <div class="text-sm text-gray-600 mb-2">Timezones reached: <span class="font-semibold">{{ stats.timezones_reached || 0 }}</span></div>
          <div v-if="(stats.top_timezones || []).length === 0" class="text-sm text-gray-500">No timezone data in range.</div>
          <div v-else>
            <div v-for="row in stats.top_timezones" :key="row.timezone" class="mb-3">
              <div class="flex justify-between text-sm mb-1"><span>{{ row.timezone }}</span><span>{{ row.count }}</span></div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#A0C5FF]" :style="{ width: geoWidth(row.count) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8">
        <h3 class="text-lg font-semibold mb-3">Top sources <span class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.top_sources" role="img" aria-label="Info">ⓘ</span></h3>
        <div v-if="(stats.top_sources || []).length === 0" class="text-sm text-gray-500">No referrer data in range.</div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div v-for="row in stats.top_sources" :key="row.source" class="mb-3">
              <div class="flex justify-between text-sm mb-1"><span>{{ row.source }}</span><span>{{ row.percent }}%</span></div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#D1FAE5]" :style="{ width: row.percent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EngagementInsightsCardsSection
        :loading="engagementInsightsLoading"
        :error="engagementInsightsError"
        :key-insights="engagementKeyInsights"
        :insight-cards="engagementInsightCards"
        :deterministic-collapsed="engagementDeterministicInsightsCollapsed"
        @refresh="$emit('load-engagement-insights')"
        @toggle-deterministic="$emit('toggle-engagement-deterministic-insights')"
      />
    </div>
  </div>
</template>

<script>
import EngagementHistoricalControlsSection from './EngagementHistoricalControlsSection.vue'
import EngagementLiveSection from './EngagementLiveSection.vue'
import EngagementInsightsCardsSection from '../shared/EngagementInsightsCardsSection.vue'
import EngagementKpiCards from '../shared/EngagementKpiCards.vue'

export default {
  name: 'EngagementPanel',
  components: {
    EngagementInsightsCardsSection,
    EngagementKpiCards,
    EngagementHistoricalControlsSection,
    EngagementLiveSection,
  },
  props: {
    liveWindows: { type: Array, default: () => [] },
    activeLiveWindow: { type: Number, default: 30 },
    liveLoading: { type: Boolean, default: false },
    liveError: { type: String, default: '' },
    liveStats: { type: Object, default: null },
    isAllScope: { type: Boolean, default: false },
    allowAllScopeHistorical: { type: Boolean, default: false },
    presets: { type: Array, default: () => [] },
    activePreset: { type: String, default: '' },
    toggleCustom: { type: Boolean, default: false },
    customFrom: { type: String, default: '' },
    customTo: { type: String, default: '' },
    exporting: { type: Boolean, default: false },
    exportError: { type: String, default: '' },
    exportStatusText: { type: String, default: '' },
    exportButtonLabel: { type: String, default: 'Export PDF' },
    loading: { type: Boolean, default: false },
    breakdownsLoading: { type: Boolean, default: false },
    engagementInsightsLoading: { type: Boolean, default: false },
    isCeremonyBreakdownLoading: { type: Boolean, default: false },
    engagementPrimaryLoadingText: { type: String, default: '' },
    errorMsg: { type: String, default: '' },
    stats: { type: Object, default: null },
    engagementHeadlineSummary: { type: Object, default: null },
    engagementSelectedScopeLabel: { type: String, default: '' },
    ceremonyDataScope: { type: String, default: 'season' },
    ceremonyViewMode: { type: String, default: 'table' },
    ceremonySortMode: { type: String, default: 'date_asc' },
    ceremonyChartMetric: { type: String, default: 'total_views' },
    ceremonyBreakdownError: { type: String, default: '' },
    ceremonyRowsSorted: { type: Array, default: () => [] },
    chartMetricLabel: { type: String, default: '' },
    engagementInsightsError: { type: String, default: '' },
    engagementKeyInsights: { type: Array, default: () => [] },
    engagementInsightCards: { type: Array, default: () => [] },
    engagementDeterministicInsightsCollapsed: { type: Boolean, default: true },
    formatWindowFn: { type: Function, required: true },
    ceremonyTitleFn: { type: Function, required: true },
    isCeremonyTopClipsLoadingFn: { type: Function, required: true },
    clipNameFn: { type: Function, required: true },
    isCeremonyExpandedFn: { type: Function, required: true },
    metricValueFn: { type: Function, required: true },
    ceremonyMetricWidthFn: { type: Function, required: true },
    geoWidthFn: { type: Function, required: true },
    tooltips: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
  },
  methods: {
    geoWidth(value) {
      return this.geoWidthFn(value)
    },
  },
}
</script>
